/**
 * Utility functions for handling select/option data lookups.
 * Works with arrays of objects that have { value, title } structure.
 */

/**
 * Find and return the entire option object from a select array.
 * @param {Array} options - Array of options with { value, title } structure
 * @param {string|number} id - The value to search for
 * @returns {Object|null} The matching option object or null if not found
 *
 * @example
 * const materials = [{ value: 1, title: "Telur" }, { value: 2, title: "Pakan" }];
 * findOption(materials, 1); // { value: 1, title: "Telur" }
 */
export const findOption = (options, id) => {
    if (!options || !id) return null;
    return options.find((opt) => String(opt.value) === String(id)) || null;
};

/**
 * Find and return only the value from a select array.
 * @param {Array} options - Array of options with { value, title } structure
 * @param {string|number} id - The value to search for
 * @returns {string|number|null} The value or null if not found
 *
 * @example
 * const materials = [{ value: 1, title: "Telur" }];
 * findOptionValue(materials, 1); // 1
 */
export const findOptionValue = (options, id) => {
    const option = findOption(options, id);
    return option?.value ?? null;
};

/**
 * Find and return only the title/label from a select array.
 * @param {Array} options - Array of options with { value, title } structure
 * @param {string|number} id - The value to search for
 * @param {string} fallback - Fallback value if not found (default: "-")
 * @returns {string} The title or fallback value
 *
 * @example
 * const materials = [{ value: 1, title: "Telur" }];
 * findOptionTitle(materials, 1); // "Telur"
 * findOptionTitle(materials, 999); // "-"
 * findOptionTitle(materials, 999, "N/A"); // "N/A"
 */
export const findOptionTitle = (options, id, fallback = "-") => {
    const option = findOption(options, id);
    return option?.title ?? fallback;
};

/**
 * Check if an option exists in a select array.
 * @param {Array} options - Array of options with { value, title } structure
 * @param {string|number} id - The value to search for
 * @returns {boolean} True if option exists, false otherwise
 *
 * @example
 * const materials = [{ value: 1, title: "Telur" }];
 * hasOption(materials, 1); // true
 * hasOption(materials, 999); // false
 */
export const hasOption = (options, id) => {
    return findOption(options, id) !== null;
};

/**
 * Get multiple option titles as a comma-separated string.
 * @param {Array} options - Array of options with { value, title } structure
 * @param {Array} ids - Array of values to search for
 * @param {string} separator - Separator between titles (default: ", ")
 * @param {string} fallback - Fallback for each missing title (default: "-")
 * @returns {string} Comma-separated titles
 *
 * @example
 * const materials = [{ value: 1, title: "Telur" }, { value: 2, title: "Pakan" }];
 * findOptionTitles(materials, [1, 2]); // "Telur, Pakan"
 */
export const findOptionTitles = (
    options,
    ids,
    separator = ", ",
    fallback = "-",
) => {
    if (!ids || !Array.isArray(ids)) return fallback;
    const titles = ids.map((id) => findOptionTitle(options, id, fallback));
    return titles.join(separator);
};
